import MapView from "@arcgis/core/views/MapView";
import Map from "@arcgis/core/Map";
import esriConfig from "@arcgis/core/config";
import { buildFineLods, scaleForZoom } from "./ZoomLevels";

// Mouse-wheel zoom tuning. Deltas are the SDK-normalised `event.deltaY` values
// (deltaMode, per-browser factor and cap already applied by the SDK).
const WHEEL_STEP = 40;           // units per half-level step (a mouse notch is 60+ on Windows, ~32 on Linux Chrome)
const WHEEL_ISOLATED_MS = 120;   // an event this long after the previous one starts a new gesture
const WHEEL_LONE_NOTCH_MS = 80;  // a small isolated event with no follow-up within this time is a lone mouse notch

export function useMapView(apikey, containerId, center, zoom) {

  esriConfig.apiKey = apikey;
  const map = new Map({
    basemap: 'arcgis-imagery'
  });

  // `zoom` (ARCGIS_MAP_ZOOM) is a standard web-mercator level. The view's own
  // zoom numbering differs (see ZoomLevels.js), so it is passed as a scale.
  // The +/- buttons, +/- keys and double-click step through `lods` whether or
  // not snapToZoom is on; leaving it off keeps goTo extent fits and touch pinch
  // continuous, as before.
  const view =  new MapView({
    container: containerId,
    map: map,
    center: center,
    scale: scaleForZoom(zoom),
    constraints: {
        lods: buildFineLods(),
        snapToZoom: false,
        minScale: scaleForZoom(8),
    },
  });

  installFineWheelZoom(view);

  return {
    view,
    map
  };
}

// Replace the SDK's mouse-wheel zoom with one that steps a single LOD (half a
// standard level) per mouse notch or per WHEEL_STEP of trackpad scrolling,
// anchored on the cursor. The SDK's own handler, once snapToZoom is on, steps one
// LOD on every wheel event regardless of size and treats a zero vertical delta
// (a horizontal trackpad swipe) as a zoom-out.
function installFineWheelZoom(view) {
  let wheelAccumulator = 0;
  let lastWheelTime = 0;
  let loneNotchTimer = null;

  // Zoom one LOD in `direction` (+1 in, -1 out), keeping the map point under
  // screen position (x, y) fixed.
  const step = (direction, x, y) => {
    const anchor = view.toMap({ x, y }); // null before the view is ready
    if (!anchor) return;

    // Step from the in-flight animation target, not the mid-animation zoom, so
    // fast spins do not drop notches. Outside an animation, round in the
    // direction of travel so a fractional zoom (after a touch pinch) steps once.
    const inflightScale = view.animation?.target?.scale;
    const base = inflightScale > 0
      ? Math.round(view.constraints.scaleToZoom(inflightScale))
      : (direction > 0 ? Math.floor(view.zoom) : Math.ceil(view.zoom));
    const targetZoom = base + direction;

    const ratio = view.constraints.zoomToScale(targetZoom) / view.scale;
    const center = view.center.clone();
    center.x = anchor.x + (center.x - anchor.x) * ratio;
    center.y = anchor.y + (center.y - anchor.y) * ratio;

    view.goTo({ center, zoom: targetZoom }, { animate: true, duration: 150 }).catch(() => {});
  };

  view.on("mouse-wheel", (event) => {
    if (!view.navigation.mouseWheelZoomEnabled) return;
    event.preventDefault();  // keep browser back-swipe and Ctrl+wheel page zoom suppressed, as the SDK does
    event.stopPropagation(); // cancel the SDK's own wheel zoom
    const delta = event.deltaY;
    if (!delta) return;      // horizontal trackpad swipe: ignore

    const isolated = event.timestamp - lastWheelTime >= WHEEL_ISOLATED_MS;
    lastWheelTime = event.timestamp;
    clearTimeout(loneNotchTimer);
    if (isolated || Math.sign(delta) !== Math.sign(wheelAccumulator)) wheelAccumulator = 0;
    wheelAccumulator += delta;

    const direction = delta < 0 ? 1 : -1;
    if (Math.abs(wheelAccumulator) >= WHEEL_STEP) {
      wheelAccumulator -= Math.sign(wheelAccumulator) * WHEEL_STEP; // keep the remainder so rapid streams step at a steady rate
      step(direction, event.x, event.y);
      return;
    }
    // A small isolated event is either a lone mouse notch (Linux Chrome and a
    // plain mouse on a Mac report well under WHEEL_STEP per notch) or the start
    // of a trackpad gesture. Step once only if nothing follows it.
    if (isolated) {
      loneNotchTimer = setTimeout(() => {
        wheelAccumulator = 0;
        step(direction, event.x, event.y);
      }, WHEEL_LONE_NOTCH_MS);
    }
  });
}
